import { gql, useMutation, useQuery } from '@apollo/client';
import Modal from '@orn/shared-web/components/Modal/Modal';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAdminGateUsers } from '../../components/admin/AdminSelectUsers/useAdminGateUsers';
import Button from '../../components/Button';
import DangerConfirmDialog from '../../components/DangerConfirmDialog';
import Header from '../../components/Header';
import { useToast } from '../../components/Toast';
import { PATHS } from '../../paths';

import { diffGate, type IGateSnapshot, type IDiffRow } from './editDiff';
import FeatureGateForm, { type IFeatureGateFormErrors, type IFeatureGateFormValues } from './FeatureGateForm';
import { findUserListOverlap, formStateToRule, ruleToFormState } from './ruleSerialization';
import { parseRuleTree } from './ruleTree';

import type {
  AdminGetFeatureGateQuery,
  AdminGetFeatureGateQueryVariables,
  AdminUpdateFeatureGateMutation,
  AdminUpdateFeatureGateMutationVariables,
  AdminDeleteFeatureGateMutation,
  AdminDeleteFeatureGateMutationVariables,
} from './__generated__/EditFeatureGateScene.gql';

const ADMIN_GET_FEATURE_GATE = gql`
  query AdminGetFeatureGate($id: ID!) {
    adminGetFeatureGate(id: $id) {
      id
      name
      description
      isEnabled
      ruleTree
      audience
      defaultWhenError
      lastModifiedByUserEmail
      createdAt
      updatedAt
    }
  }
`;

const ADMIN_UPDATE_FEATURE_GATE = gql`
  mutation AdminUpdateFeatureGate($id: ID!, $input: UpdateFeatureGateInput!) {
    adminUpdateFeatureGate(id: $id, input: $input) {
      id
      name
      description
      isEnabled
      ruleTree
      audience
      defaultWhenError
      lastModifiedByUserEmail
      updatedAt
    }
  }
`;

const ADMIN_DELETE_FEATURE_GATE = gql`
  mutation AdminDeleteFeatureGate($id: ID!) {
    adminDeleteFeatureGate(id: $id)
  }
`;

type LoadedGate = NonNullable<AdminGetFeatureGateQuery['adminGetFeatureGate']>;

/**
 * Edit-form scene for a single feature gate. Wraps the shared
 * `FeatureGateForm` with three distinct confirmation flows:
 *
 *  - **Edit-save** (low friction): on submit, opens a diff dialog so the
 *    operator sees exactly what will change before committing.
 *  - **Disable** (high friction, typed): kill-switch flip from on→off
 *    routes through the shared `DangerConfirmDialog`. Re-enabling stays
 *    on the low-friction path.
 *  - **Delete** (high friction, typed): the secondary "Delete" action
 *    opens the same typed-confirm dialog with delete copy.
 *
 * Renders a non-blocking banner when the persisted rule tree does not
 * match a shape the v1 form can represent (e.g. a hand-edited
 * `country_in` rule). The form's other fields stay editable; the
 * rollout sliders display zero until v2 ships the recursive builder.
 */
const EditFeatureGateScene = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, loading, error } = useQuery<AdminGetFeatureGateQuery, AdminGetFeatureGateQueryVariables>(
    ADMIN_GET_FEATURE_GATE,
    {
      variables: { id: id ?? '' },
      skip: !id,
      fetchPolicy: 'cache-and-network',
    }
  );

  const gate = data?.adminGetFeatureGate ?? null;
  const parsedRule = React.useMemo(() => (gate ? parseRuleTree(gate.ruleTree) : null), [gate]);
  const ruleParseResult = React.useMemo(
    () =>
      parsedRule
        ? ruleToFormState(parsedRule)
        : { adminPct: 0, everyonePct: 0, allowUserIds: [], denyUserIds: [], isCanonical: false },
    [parsedRule]
  );

  const [values, setValues] = React.useState<IFeatureGateFormValues | null>(null);
  const [errors, setErrors] = React.useState<IFeatureGateFormErrors>({});

  // Hydrate form state once the gate has loaded. Re-syncing on every
  // render would clobber the operator's in-progress edits on a refetch.
  // Tying re-sync to gate id + updatedAt means a successful save (which
  // bumps updatedAt server-side) refreshes the form to the persisted
  // shape, preserving the post-save snapshot as the new baseline.
  React.useEffect(() => {
    if (!gate) {
      return;
    }
    setValues({
      name: gate.name,
      description: gate.description,
      adminPct: ruleParseResult.adminPct,
      everyonePct: ruleParseResult.everyonePct,
      allowUserIds: ruleParseResult.allowUserIds,
      denyUserIds: ruleParseResult.denyUserIds,
      isEnabled: gate.isEnabled,
      audience: gate.audience,
      defaultWhenError: gate.defaultWhenError,
    });
    setErrors({});
    // `ruleParseResult` is memoized on the parsed rule (keyed on `gate`),
    // so its identity is stable until the gate reloads — depending on the
    // object is equivalent to depending on each field.
  }, [gate, ruleParseResult]);

  if (!id) {
    return <NotFound message="Missing gate id in URL." onBack={() => navigate(PATHS.FEATURE_GATES)} />;
  }
  if (loading && !gate) {
    return (
      <Shell>
        <span className="text-fg-secondary text-xs">Loading…</span>
      </Shell>
    );
  }
  if (error) {
    return <NotFound message="Failed to load gate." onBack={() => navigate(PATHS.FEATURE_GATES)} />;
  }
  if (!gate) {
    return <NotFound message="No gate with that id." onBack={() => navigate(PATHS.FEATURE_GATES)} />;
  }
  if (!values) {
    return (
      <Shell>
        <span className="text-fg-secondary text-xs">Loading…</span>
      </Shell>
    );
  }

  return (
    <EditFeatureGateContent
      gate={gate}
      values={values}
      setValues={setValues}
      errors={errors}
      setErrors={setErrors}
      isCanonical={ruleParseResult.isCanonical}
      onAfterSave={() => navigate(PATHS.FEATURE_GATES)}
      onAfterDelete={() => navigate(PATHS.FEATURE_GATES)}
      onBack={() => navigate(PATHS.FEATURE_GATES)}
    />
  );
};

interface IContentProps {
  gate: LoadedGate;
  values: IFeatureGateFormValues;
  setValues: React.Dispatch<React.SetStateAction<IFeatureGateFormValues | null>>;
  errors: IFeatureGateFormErrors;
  setErrors: React.Dispatch<React.SetStateAction<IFeatureGateFormErrors>>;
  isCanonical: boolean;
  onAfterSave: () => void;
  onAfterDelete: () => void;
  onBack: () => void;
}

const EditFeatureGateContent = ({
  gate,
  values,
  setValues,
  errors,
  setErrors,
  isCanonical,
  onAfterSave,
  onAfterDelete,
  onBack,
}: IContentProps) => {
  const { showToast } = useToast();
  const [updateGate, { loading: isSaving }] = useMutation<
    AdminUpdateFeatureGateMutation,
    AdminUpdateFeatureGateMutationVariables
  >(ADMIN_UPDATE_FEATURE_GATE);
  const [deleteGate] = useMutation<AdminDeleteFeatureGateMutation, AdminDeleteFeatureGateMutationVariables>(
    ADMIN_DELETE_FEATURE_GATE
  );

  // Resolve allow/deny ids to emails for the confirm dialog. Reads the
  // same `adminListUsers` query the pickers run, so it's a cache hit.
  const { users } = useAdminGateUsers();
  const userEmailById = React.useMemo(() => new Map(users.map((u) => [u.id, u.email])), [users]);

  const [editConfirmRows, setEditConfirmRows] = React.useState<IDiffRow[] | null>(null);
  const [pendingValues, setPendingValues] = React.useState<IFeatureGateFormValues | null>(null);
  const [showDisableDialog, setShowDisableDialog] = React.useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);

  // Esc bails back to the list view, but only when no dialog is open
  // and the operator isn't typing in a form field. react-aria's Modal
  // already consumes Esc to close itself, so keying out of a dialog
  // closes the dialog first; pressing Esc again then bails to the list.
  const isAnyDialogOpen = editConfirmRows !== null || showDisableDialog || showDeleteDialog;
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }
      if (isAnyDialogOpen) {
        return;
      }
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      e.preventDefault();
      onBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isAnyDialogOpen, onBack]);

  // Use the parsed rule tree (typed `FeatureGateRuleNode`) when available
  // rather than `gate.ruleTree`, which codegen surfaces as `any` for the
  // `JSONObject` scalar. Falling back to a degenerate `all_users@0` leaf
  // when parse fails keeps the diff helper typed without crashing the
  // page on a malformed payload (the non-canonical banner is already up
  // in that branch — the diff just won't show meaningful rule rows).
  const parsedPrevRule = parseRuleTree(gate.ruleTree);
  const prevSnapshot: IGateSnapshot = {
    isEnabled: gate.isEnabled,
    ruleTree: parsedPrevRule ?? { type: 'all_users', rolloutPercent: 0 },
    audience: gate.audience,
    defaultWhenError: gate.defaultWhenError,
    description: gate.description,
  };

  const handleChange = (patch: Partial<IFeatureGateFormValues>) => {
    setValues((prev) => (prev ? { ...prev, ...patch } : prev));
    setErrors({});
  };

  const validate = (): IFeatureGateFormErrors | null => {
    const nextErrors: IFeatureGateFormErrors = {};
    const hasGrant = values.adminPct > 0 || values.everyonePct > 0 || values.allowUserIds.length > 0;
    if (isCanonical && !hasGrant && values.isEnabled) {
      nextErrors.rollout =
        values.denyUserIds.length > 0
          ? 'A deny-list only restricts an existing rollout. Set Everyone to 100% to allow all users except the deny-list, or add a rollout % / allow-list.'
          : 'Set a rollout %, add an allow-list, or use the kill switch to disable.';
    } else if (isCanonical && findUserListOverlap(values.allowUserIds, values.denyUserIds).length > 0) {
      nextErrors.rollout = 'A user can’t be on both the allow-list and the deny-list. Remove them from one.';
    }
    return Object.keys(nextErrors).length > 0 ? nextErrors : null;
  };

  const buildNextSnapshot = (): IGateSnapshot | null => {
    // For non-canonical rules we leave the existing tree untouched —
    // the form can't represent it, so we can't safely re-emit it.
    const nextRule = isCanonical
      ? formStateToRule(values.adminPct, values.everyonePct, values.allowUserIds, values.denyUserIds)
      : gate.ruleTree;
    if (!nextRule) {
      // Reachable when the form has both sliders at 0 and the gate is
      // canonical — caller should already have surfaced the rollout
      // error via `validate`.
      return null;
    }
    return {
      isEnabled: values.isEnabled,
      ruleTree: nextRule,
      audience: values.audience,
      defaultWhenError: values.defaultWhenError,
      description: values.description,
    };
  };

  const handleSubmit = () => {
    const v = validate();
    if (v) {
      setErrors(v);
      return;
    }
    const next = buildNextSnapshot();
    if (!next) {
      setErrors({ rollout: 'Set a rollout %, or use the kill switch to disable.' });
      return;
    }
    const rows = diffGate(prevSnapshot, next, userEmailById);
    if (rows.length === 0) {
      showToast({ type: 'info', message: 'No changes to save.' });
      return;
    }
    // Disabling-an-enabled-gate flips through the high-friction dialog,
    // not the diff confirm. The disable dialog routes back through the
    // same update mutation on confirm.
    if (gate.isEnabled && !values.isEnabled) {
      setPendingValues(values);
      setShowDisableDialog(true);
      return;
    }
    setPendingValues(values);
    setEditConfirmRows(rows);
  };

  const performUpdate = async (snapshot: IGateSnapshot) => {
    await updateGate({
      variables: {
        id: gate.id,
        input: {
          description: snapshot.description,
          isEnabled: snapshot.isEnabled,
          ruleTree: snapshot.ruleTree,
          defaultWhenError: snapshot.defaultWhenError,
          // audience is not in UpdateFeatureGateInput by design — see
          // the comment in mops-api/FeatureGateInputs.ts. The form
          // forces this field read-only, but we still don't forward it.
        },
      },
    });
  };

  const handleConfirmEditSave = async () => {
    const next = buildNextSnapshot();
    if (!next) {
      return;
    }
    try {
      await performUpdate(next);
      showToast({ type: 'success', message: `Saved ${gate.name}.` });
      setEditConfirmRows(null);
      setPendingValues(null);
      onAfterSave();
    } catch {
      showToast({ type: 'error', message: 'Failed to save changes.' });
    }
  };

  const handleConfirmDisable = async () => {
    const next = buildNextSnapshot();
    if (!next) {
      throw new Error('no snapshot');
    }
    try {
      await performUpdate(next);
      showToast({ type: 'success', message: `Disabled ${gate.name}.` });
      setPendingValues(null);
      onAfterSave();
    } catch (err) {
      showToast({ type: 'error', message: 'Failed to disable gate.' });
      throw err;
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteGate({ variables: { id: gate.id } });
      showToast({ type: 'success', message: `Deleted ${gate.name}.` });
      onAfterDelete();
    } catch (err) {
      showToast({ type: 'error', message: `Failed to delete ${gate.name}.` });
      throw err;
    }
  };

  return (
    <Shell>
      <div className="flex items-baseline gap-3">
        <h1 className="text-sm font-semibold uppercase tracking-widest">Feature gate</h1>
        <span className="text-fg-secondary font-mono text-sm">{gate.name}</span>
        <button
          type="button"
          onClick={onBack}
          className="text-fg-tertiary hover:text-fg-primary focus-visible:outline-border-brand ml-auto text-xs uppercase tracking-wider focus-visible:outline-2 focus-visible:-outline-offset-1"
        >
          ← Back to list
        </button>
      </div>

      {/* Cache-propagation advisory — `FeatureGateController` keeps a
          5-minute per-process cache (see TTL_MS in
          packages/core/src/controllers/featureGate/FeatureGateController.ts). The
          writing process invalidates its own cache on success, but
          peer processes serve stale rows until their own TTL elapses,
          so cross-fleet propagation is bounded by 5 minutes. */}
      <div className="border-fg-warning-primary bg-bg-warning-primary/10 flex flex-col gap-1 border-l-2 px-3 py-2">
        <span className="text-fg-warning-primary text-xs font-semibold uppercase tracking-wider">
          Changes can take up to 5 minutes to propagate
        </span>
        <span className="text-fg-secondary text-xs leading-relaxed">
          App servers hold a 5-minute in-process cache of feature-gate state. After saving, allow up to 5 minutes for
          create, edit, disable, or delete actions to be live across every server.
        </span>
      </div>

      {!isCanonical && (
        <div className="border-fg-warning-primary bg-bg-warning-primary/10 flex flex-col gap-1 border-l-2 px-3 py-2">
          <span className="text-fg-warning-primary text-xs font-semibold uppercase tracking-wider">
            Custom rule shape
          </span>
          <span className="text-fg-secondary text-xs leading-relaxed">
            This gate uses a rule shape the v1 form can't represent. Edit the rule tree directly via mops-api GraphQL
            until the v2 builder ships. Other fields below stay editable.
          </span>
        </div>
      )}

      <FeatureGateForm values={values} errors={errors} onChange={handleChange} readOnlyNameAndAudience />

      <div className="border-border-primary flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <Button color="danger" size="sm" onClick={() => setShowDeleteDialog(true)} isDisabled={isSaving}>
          Delete gate
        </Button>
        <div className="flex gap-2">
          <Button color="secondary" size="sm" onClick={onBack} isDisabled={isSaving}>
            Cancel
          </Button>
          <Button color="primary" size="sm" onClick={handleSubmit} isLoading={isSaving}>
            Save
          </Button>
        </div>
      </div>

      {/* Edit-save diff confirmation — low friction, single button */}
      <Modal
        isOpen={editConfirmRows !== null && pendingValues !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditConfirmRows(null);
            setPendingValues(null);
          }
        }}
        size="xl"
      >
        <div className="flex w-full flex-col">
          <div className="border-border-primary border-b px-4 py-3">
            <span className="text-fg-secondary text-xs font-semibold uppercase tracking-widest">Confirm changes</span>
          </div>
          <div className="flex flex-col gap-3 px-4 py-4">
            <table className="w-full table-fixed text-xs tabular-nums">
              <tbody>
                {(editConfirmRows ?? []).map((row, idx) => (
                  <tr key={`${row.field}-${idx}`} className="border-border-primary border-b last:border-b-0">
                    <td className="text-fg-tertiary w-24 py-1.5 pr-2 align-top uppercase tracking-wider">
                      {row.label}
                    </td>
                    <td className="text-fg-secondary whitespace-pre-line break-words py-1.5 pr-1.5 align-top font-mono">
                      {row.before || '—'}
                    </td>
                    <td className="text-fg-tertiary w-5 py-1.5 pr-1.5 align-top">→</td>
                    <td className="text-fg-primary whitespace-pre-line break-words py-1.5 align-top font-mono">
                      {row.after || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                color="secondary"
                size="sm"
                onClick={() => {
                  setEditConfirmRows(null);
                  setPendingValues(null);
                }}
                isDisabled={isSaving}
              >
                Cancel
              </Button>
              <Button color="primary" size="sm" onClick={() => void handleConfirmEditSave()} isLoading={isSaving}>
                Save changes
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <DangerConfirmDialog
        isOpen={showDisableDialog}
        onOpenChange={setShowDisableDialog}
        confirmValue={gate.name}
        title="Disable feature gate"
        body={
          <>
            Disabling this gate immediately removes access for every user it currently passes for. To proceed, type the
            gate name.
          </>
        }
        confirmLabel="Disable"
        onConfirm={handleConfirmDisable}
      />

      <DangerConfirmDialog
        isOpen={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        confirmValue={gate.name}
        title="Delete feature gate"
        body={
          <>
            Deleting this gate immediately terminates access for every consumer still calling{' '}
            <span className="font-mono">gatePasses('{gate.name}')</span>. Server-side calls will fall back to the gate's{' '}
            <span className="font-mono">defaultValue</span>. To proceed, type the gate name.
          </>
        }
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
      />
    </Shell>
  );
};

const Shell = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
    <Header />
    <main className="flex flex-1 flex-col gap-5 px-6 py-5">{children}</main>
  </div>
);

const NotFound = ({ message, onBack }: { message: string; onBack: () => void }) => (
  <Shell>
    <div className="flex flex-col gap-3">
      <span className="text-fg-error-primary text-sm">{message}</span>
      <Button color="secondary" size="sm" onClick={onBack}>
        Back to list
      </Button>
    </div>
  </Shell>
);

export default EditFeatureGateScene;
